/* ============================================================
   COMPLIANCE PDF GENERATOR

   Produces a single downloadable PDF combining:
   1. A natively-typeset Compliance Training Record (selectable text,
      proper headers, signature block, page numbers)
   2. All referenced attachments stitched in after the dossier:
      - PDF attachments: pages copied verbatim via pdf-lib
      - Image attachments: embedded as full-page images
      - Other file types: listed as "binary attachment — see file
        bundle" with metadata

   Each attachment is preceded by a separator page identifying it.

   Public API: buildCompliancePdf(input) -> Promise<Blob>
   ============================================================ */

import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";

/* ---------- helpers ---------- */
const PAGE_W = 612; // 8.5" * 72
const PAGE_H = 792; // 11"  * 72
const MARGIN_L = 54;
const MARGIN_R = 54;
const MARGIN_T = 60;
const MARGIN_B = 60;
const USABLE_W = PAGE_W - MARGIN_L - MARGIN_R;
const FOOT_HEIGHT = 30;

const COLORS = {
  text: rgb(0.067, 0.067, 0.067),
  dim: rgb(0.333, 0.333, 0.333),
  dimmer: rgb(0.5, 0.5, 0.5),
  gold: rgb(0.541, 0.408, 0),         // #8a6800
  goldBg: rgb(0.98, 0.96, 0.88),
  rule: rgb(0.75, 0.75, 0.75),
  panelBg: rgb(0.97, 0.97, 0.97),
  red: rgb(0.69, 0, 0),
  green: rgb(0.10, 0.42, 0.23),
  blue: rgb(0.16, 0.29, 0.48),
  white: rgb(1, 1, 1),
};

/* ---------- date formatting ---------- */
function fmtDateFull(iso) {
  if (!iso) return "—";
  let d;
  if (typeof iso === "string" && iso.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    d = new Date(iso + "T00:00:00");
  } else {
    d = new Date(iso);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  let d;
  if (typeof iso === "string" && iso.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    d = new Date(iso + "T00:00:00");
  } else {
    d = new Date(iso);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/* WinAnsi-safe text. The standard PDF fonts (Times, Helvetica, Courier) only
   support the WinAnsi character set. Replace common Unicode characters that
   appear in app text with ASCII equivalents so they can be drawn. */
function safeText(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/[\u2192\u279C\u27A4\u27F6]/g, "->")     // various right arrows
    .replace(/[\u2190\u27F5]/g, "<-")                  // left arrows
    .replace(/[\u2194\u27F7]/g, "<->")                 // double arrows
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")       // curly single quotes
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')       // curly double quotes
    .replace(/[\u2013\u2014]/g, "-")                   // en/em dashes
    .replace(/\u2026/g, "...")                          // ellipsis
    .replace(/\u00B7/g, "·")                            // middle dot (WinAnsi-OK, keep)
    .replace(/\u2022/g, "•")                            // bullet (WinAnsi-OK, keep)
    .replace(/\u00A0/g, " ")                            // non-breaking space
    .replace(/[\u200B-\u200D\uFEFF]/g, "")             // zero-width spaces
    // Strip any remaining non-WinAnsi characters as a last resort
    .replace(/[^\x00-\xFF]/g, (c) => {
      // Specific allowed extended chars (WinAnsi has these in 0x80-0xFF range)
      const code = c.charCodeAt(0);
      if (code === 0x2014 || code === 0x2013) return "-";
      // Common accented Latin chars are in WinAnsi already (handled by 0x00-0xFF)
      return "?";
    });
}

/* ---------- low-level layout ---------- */
class Layout {
  constructor(pdf, fonts) {
    this.pdf = pdf;
    this.fonts = fonts;
    this.page = null;
    this.y = 0;
    this.meta = null; // { reportId, employeeName, asOfDate, generatedDate, page#, total }
    this.startNewPage();
  }
  setMeta(m) { this.meta = m; }
  startNewPage() {
    this.page = this.pdf.addPage([PAGE_W, PAGE_H]);
    this.y = PAGE_H - MARGIN_T;
    if (this.meta) this.drawRunningHeader();
  }
  drawRunningHeader() {
    const f = this.fonts.sans;
    const fs = 8;
    this.page.drawText("LAS VEGAS MINI GRAND PRIX", {
      x: MARGIN_L, y: PAGE_H - 32, size: fs, font: this.fonts.sansBold, color: COLORS.dimmer,
    });
    const rightLbl = "Compliance Training Record";
    const w = f.widthOfTextAtSize(rightLbl, fs);
    this.page.drawText(rightLbl, {
      x: PAGE_W - MARGIN_R - w, y: PAGE_H - 32, size: fs, font: f, color: COLORS.dimmer,
    });
    this.page.drawLine({
      start: { x: MARGIN_L, y: PAGE_H - 40 }, end: { x: PAGE_W - MARGIN_R, y: PAGE_H - 40 },
      thickness: 0.5, color: COLORS.rule,
    });
  }
  drawRunningFooter(pageNum, totalPages) {
    const fs = 7.5;
    const f = this.fonts.mono;
    const meta = this.meta || {};
    const left = meta.reportId || "";
    const mid = `Page ${pageNum} of ${totalPages}`;
    const right = meta.generatedDate ? `Generated ${meta.generatedDate}` : "";
    this.page.drawLine({
      start: { x: MARGIN_L, y: MARGIN_B - 12 }, end: { x: PAGE_W - MARGIN_R, y: MARGIN_B - 12 },
      thickness: 0.5, color: COLORS.rule,
    });
    this.page.drawText(left, {
      x: MARGIN_L, y: MARGIN_B - 24, size: fs, font: f, color: COLORS.dimmer,
    });
    const midW = f.widthOfTextAtSize(mid, fs);
    this.page.drawText(mid, {
      x: (PAGE_W - midW) / 2, y: MARGIN_B - 24, size: fs, font: f, color: COLORS.dimmer,
    });
    const rightW = f.widthOfTextAtSize(right, fs);
    this.page.drawText(right, {
      x: PAGE_W - MARGIN_R - rightW, y: MARGIN_B - 24, size: fs, font: f, color: COLORS.dimmer,
    });
  }
  /* Ensure we have at least `needed` vertical space; otherwise new page */
  reserve(needed) {
    if (this.y - needed < MARGIN_B + FOOT_HEIGHT) this.startNewPage();
  }
  /* Move y down by `n` */
  advance(n) { this.y -= n; }
  /* Draw a text line, wrapping if needed. Returns total height consumed. */
  drawWrapped(text, opts = {}) {
    const {
      font = this.fonts.serif,
      size = 11,
      color = COLORS.text,
      maxWidth = USABLE_W,
      lineHeight = 1.4,
      x = MARGIN_L,
      align = "left",
    } = opts;
    if (!text) return 0;
    const lines = wrapText(text, font, size, maxWidth);
    const lh = size * lineHeight;
    let consumed = 0;
    for (const ln of lines) {
      this.reserve(lh);
      let drawX = x;
      if (align === "center") {
        const w = font.widthOfTextAtSize(ln, size);
        drawX = (PAGE_W - w) / 2;
      } else if (align === "right") {
        const w = font.widthOfTextAtSize(ln, size);
        drawX = PAGE_W - MARGIN_R - w;
      }
      this.page.drawText(ln, { x: drawX, y: this.y - size, size, font, color });
      this.advance(lh);
      consumed += lh;
    }
    return consumed;
  }
  /* Horizontal rule */
  rule(opts = {}) {
    const { color = COLORS.rule, thickness = 0.5, gap = 6 } = opts;
    this.reserve(gap * 2 + thickness);
    this.advance(gap);
    this.page.drawLine({
      start: { x: MARGIN_L, y: this.y }, end: { x: PAGE_W - MARGIN_R, y: this.y },
      thickness, color,
    });
    this.advance(gap);
  }
}

/* Word-wrap helper that respects PDF font metrics */
function wrapText(text, font, size, maxWidth) {
  const lines = [];
  const safe = safeText(text);
  // Honor manual line breaks first
  for (const para of safe.split("\n")) {
    if (!para) { lines.push(""); continue; }
    const words = para.split(/\s+/);
    let cur = "";
    for (const w of words) {
      const trial = cur ? cur + " " + w : w;
      const width = font.widthOfTextAtSize(trial, size);
      if (width > maxWidth && cur) {
        lines.push(cur);
        cur = w;
      } else {
        cur = trial;
      }
    }
    if (cur) lines.push(cur);
  }
  return lines;
}

/* ---------- dossier sections ---------- */
function drawTitleBlock(L, input) {
  L.advance(20);
  L.drawWrapped("LAS VEGAS MINI GRAND PRIX", {
    font: L.fonts.sansBold, size: 9, color: COLORS.dimmer, align: "center", lineHeight: 1.2,
  });
  L.advance(8);
  L.drawWrapped("Compliance Training Record", {
    font: L.fonts.serifBold, size: 22, color: COLORS.text, align: "center", lineHeight: 1.2,
  });
  L.advance(2);
  L.drawWrapped("Point-in-Time Certification & Audit Trail", {
    font: L.fonts.serifItalic, size: 12, color: COLORS.dim, align: "center", lineHeight: 1.2,
  });
  L.advance(8);
  L.drawWrapped(`Report ID: ${input.reportId}`, {
    font: L.fonts.mono, size: 9, color: COLORS.dimmer, align: "center", lineHeight: 1.2,
  });
  // Gold rule under the title
  L.advance(8);
  L.reserve(8);
  L.page.drawLine({
    start: { x: MARGIN_L, y: L.y }, end: { x: PAGE_W - MARGIN_R, y: L.y },
    thickness: 1.5, color: COLORS.gold,
  });
  L.advance(20);
}

function drawAttestation(L, input) {
  // Light-gray box around the attestation table
  const startY = L.y;
  const padX = 14, padY = 12;
  const rows = [
    ["Subject employee", `${input.employee.name}${input.employee.role ? " · " + input.employee.role : ""}${input.employee.department ? " · " + input.employee.department : ""}`],
    ["As-of date", fmtDateFull(input.asOfDate)],
    ["Report generated", fmtDateFull(input.generatedDate)],
    ["Rides covered", input.ridesCoveredText || "All rides"],
  ];
  if (input.incidentRef) {
    rows.push(["Prepared in reference to", input.incidentRef]);
  }
  // Compute total height
  const labelW = 165;
  const valW = USABLE_W - labelW - padX * 2;
  const rowH = 11 * 1.55; // size * line height for serif body
  let totalRowsH = 0;
  for (const [, val] of rows) {
    const wrapped = wrapText(val, L.fonts.serif, 11, valW);
    totalRowsH += Math.max(rowH, wrapped.length * rowH);
  }
  const boxH = padY * 2 + totalRowsH;
  L.reserve(boxH);
  // Draw box
  L.page.drawRectangle({
    x: MARGIN_L, y: L.y - boxH, width: USABLE_W, height: boxH,
    color: COLORS.panelBg, borderColor: COLORS.rule, borderWidth: 0.5,
  });
  // Draw rows
  let rowY = L.y - padY;
  for (const [label, value] of rows) {
    L.page.drawText(label.toUpperCase(), {
      x: MARGIN_L + padX, y: rowY - 9, size: 8, font: L.fonts.sansBold, color: COLORS.dimmer,
    });
    const wrapped = wrapText(value, L.fonts.serif, 11, valW);
    let lineY = rowY - 9;
    for (const ln of wrapped) {
      L.page.drawText(ln, {
        x: MARGIN_L + padX + labelW, y: lineY, size: 11, font: L.fonts.serif, color: COLORS.text,
      });
      lineY -= rowH;
    }
    rowY -= Math.max(rowH, wrapped.length * rowH);
  }
  L.y = L.y - boxH;
  L.advance(20);
}

function drawSectionTitle(L, text) {
  L.reserve(28);
  L.drawWrapped(text, {
    font: L.fonts.serifBold, size: 14, color: COLORS.text, lineHeight: 1.2,
  });
  L.advance(2);
  L.reserve(4);
  L.page.drawLine({
    start: { x: MARGIN_L, y: L.y }, end: { x: PAGE_W - MARGIN_R, y: L.y },
    thickness: 0.5, color: COLORS.rule,
  });
  L.advance(10);
}

function drawCertSummary(L, input) {
  drawSectionTitle(L, `Certification Status as of ${fmtDateFull(input.asOfDate)}`);
  L.drawWrapped(
    "Based on training, sign-off, observation, and certification records maintained by Las Vegas Mini Grand Prix, the subject employee held the following certification levels on the as-of date:",
    { font: L.fonts.serif, size: 11, color: COLORS.text, lineHeight: 1.55 }
  );
  L.advance(8);
  // Table: Ride | Level | Basis
  const colW = [USABLE_W * 0.30, USABLE_W * 0.20, USABLE_W * 0.50];
  const rowH = 18;
  // Header
  L.reserve(rowH);
  L.page.drawRectangle({
    x: MARGIN_L, y: L.y - rowH, width: USABLE_W, height: rowH,
    color: COLORS.panelBg,
  });
  L.page.drawLine({
    start: { x: MARGIN_L, y: L.y - rowH }, end: { x: PAGE_W - MARGIN_R, y: L.y - rowH },
    thickness: 1, color: COLORS.rule,
  });
  const headers = ["Ride", "Level", "Basis"];
  let cx = MARGIN_L + 8;
  for (let i = 0; i < headers.length; i++) {
    L.page.drawText(headers[i].toUpperCase(), {
      x: cx, y: L.y - rowH + 5, size: 8, font: L.fonts.sansBold, color: COLORS.dimmer,
    });
    cx += colW[i];
  }
  L.advance(rowH);
  // Data rows
  for (const rg of input.rides) {
    const lvl = rg.assertion?.level && rg.assertion.level !== "0" ? rg.assertion.level : null;
    const basis = rg.assertion?.basis === "no_records"
      ? "No certification records on or before this date."
      : rg.assertion?.supportingEvents?.length > 0
      ? `Established by ${rg.assertion.supportingEvents.length} record${rg.assertion.supportingEvents.length === 1 ? "" : "s"} on file.`
      : "—";
    // Compute row height based on basis wrapping
    const basisLines = wrapText(basis, L.fonts.serif, 10, colW[2] - 16);
    const thisRowH = Math.max(rowH, basisLines.length * 13 + 8);
    L.reserve(thisRowH);
    L.page.drawLine({
      start: { x: MARGIN_L, y: L.y - thisRowH }, end: { x: PAGE_W - MARGIN_R, y: L.y - thisRowH },
      thickness: 0.5, color: COLORS.rule,
    });
    // Ride name (bold serif)
    L.page.drawText(rg.ride.label, {
      x: MARGIN_L + 8, y: L.y - 13, size: 11, font: L.fonts.serifBold, color: COLORS.text,
    });
    // Level (in colored badge if certified)
    const lvlX = MARGIN_L + colW[0] + 8;
    if (lvl) {
      const lvlColor =
        lvl === "T" ? rgb(0.85, 0.55, 0.10) :
        lvl === "L2" ? rgb(0.16, 0.42, 0.83) :
        lvl === "L3" ? rgb(0.10, 0.42, 0.23) :
        lvl === "L4" ? rgb(0.45, 0.16, 0.55) :
        COLORS.dim;
      const lvlW = L.fonts.sansBold.widthOfTextAtSize(lvl, 9) + 12;
      L.page.drawRectangle({
        x: lvlX, y: L.y - 16, width: lvlW, height: 14,
        color: COLORS.white, borderColor: lvlColor, borderWidth: 1,
      });
      L.page.drawText(lvl, {
        x: lvlX + 6, y: L.y - 13, size: 9, font: L.fonts.sansBold, color: lvlColor,
      });
    } else {
      L.page.drawText("Not certified", {
        x: lvlX, y: L.y - 13, size: 10, font: L.fonts.serifItalic, color: COLORS.dim,
      });
    }
    // Basis (wrapped)
    let basisY = L.y - 13;
    for (const ln of basisLines) {
      L.page.drawText(ln, {
        x: MARGIN_L + colW[0] + colW[1] + 8, y: basisY, size: 10, font: L.fonts.serif, color: COLORS.dim,
      });
      basisY -= 13;
    }
    L.advance(thisRowH);
  }
  L.advance(10);
  L.drawWrapped(
    "Certification levels above were computed by chronologically replaying sign-off, quiz, observation, and training records on file on or before the as-of date. The complete audit trail supporting each ride's certification is provided in the sections that follow. All referenced supporting documents are attached after the audit trail.",
    { font: L.fonts.serif, size: 11, color: COLORS.text, lineHeight: 1.55 }
  );
  L.advance(14);
}

function drawAuditTrail(L, input) {
  drawSectionTitle(L, "Audit Trail by Ride");
  for (const rg of input.rides) {
    drawRideBlock(L, rg, input.asOfDate);
  }
}

function drawRideBlock(L, rg, asOfDate) {
  if (rg.events.length === 0 && rg.docs.length === 0) {
    // Skip empty rides in compact form
    return;
  }
  L.reserve(60);
  // Ride header
  L.page.drawLine({
    start: { x: MARGIN_L, y: L.y - 2 }, end: { x: MARGIN_L + 4, y: L.y - 2 - 18 },
    thickness: 0, color: COLORS.dim,
  });
  L.page.drawRectangle({
    x: MARGIN_L, y: L.y - 22, width: 3, height: 22,
    color: COLORS.dim,
  });
  L.page.drawText(rg.ride.label, {
    x: MARGIN_L + 12, y: L.y - 16, size: 13, font: L.fonts.serifBold, color: COLORS.text,
  });
  // Cert status on right
  const lvl = rg.assertion?.level && rg.assertion.level !== "0" ? rg.assertion.level : null;
  const statusText = lvl
    ? `Certified as of ${fmtDateFull(asOfDate)}: ${lvl}`
    : `Not certified as of ${fmtDateFull(asOfDate)}`;
  const stW = L.fonts.serif.widthOfTextAtSize(statusText, 10);
  L.page.drawText(statusText, {
    x: PAGE_W - MARGIN_R - stW, y: L.y - 14, size: 10, font: L.fonts.serif, color: COLORS.dim,
  });
  L.advance(28);
  // Event rows
  for (const ev of rg.events) {
    drawEventRow(L, ev);
  }
  // Document rows
  for (const d of rg.docs) {
    drawDocRow(L, d);
  }
  L.advance(10);
}

function drawEventRow(L, ev) {
  const dateCol = 80;
  const typeCol = 75;
  const detailX = MARGIN_L + dateCol + typeCol + 4;
  const detailW = PAGE_W - MARGIN_R - detailX;
  const detailLines = wrapText(ev.detail, L.fonts.serif, 10, detailW);
  const rowH = Math.max(16, detailLines.length * 13 + 4);
  L.reserve(rowH);
  // Date
  L.page.drawText(fmtDateShort(ev.date), {
    x: MARGIN_L, y: L.y - 12, size: 9, font: L.fonts.mono, color: COLORS.dimmer,
  });
  // Type pill
  const typeBg =
    ev.type === "Sign-off" ? COLORS.green :
    ev.type === "Quiz" ? COLORS.blue :
    ev.type === "Observation" ? rgb(0.45, 0.16, 0.55) :
    COLORS.dim;
  const typeW = L.fonts.sansBold.widthOfTextAtSize(ev.type, 8) + 10;
  L.page.drawRectangle({
    x: MARGIN_L + dateCol, y: L.y - 13, width: typeW, height: 11,
    color: COLORS.white, borderColor: typeBg, borderWidth: 0.7,
  });
  L.page.drawText(ev.type, {
    x: MARGIN_L + dateCol + 5, y: L.y - 11, size: 8, font: L.fonts.sansBold, color: typeBg,
  });
  // Detail
  let dy = L.y - 12;
  for (const ln of detailLines) {
    L.page.drawText(ln, {
      x: detailX, y: dy, size: 10, font: L.fonts.serif, color: COLORS.text,
    });
    dy -= 13;
  }
  L.advance(rowH);
}

function drawDocRow(L, d) {
  const detailX = MARGIN_L + 80;
  const detailW = PAGE_W - MARGIN_R - detailX;
  // Title line: category + filename
  const titleLine = `[${d.category}] ${d.filename}`;
  const titleLines = wrapText(titleLine, L.fonts.serifBold, 10, detailW);
  // Sub line: uploaded / issued / expires
  const subParts = [`Uploaded ${fmtDateFull(d.uploaded_at)}`];
  if (d.issued_at) subParts.push(`Issued ${fmtDateFull(d.issued_at)}`);
  if (d.expires_at) subParts.push(`Expires ${fmtDateFull(d.expires_at)}`);
  const subLine = subParts.join(" · ");
  const subLines = wrapText(subLine, L.fonts.serifItalic, 9, detailW);
  const descLines = d.description ? wrapText(d.description, L.fonts.serifItalic, 9, detailW) : [];
  const rowH = (titleLines.length * 13) + (subLines.length * 11) + (descLines.length * 11) + 6;
  L.reserve(rowH);
  // Date column
  L.page.drawText(fmtDateShort(d.uploaded_at), {
    x: MARGIN_L, y: L.y - 12, size: 9, font: L.fonts.mono, color: COLORS.dimmer,
  });
  // Doc pill
  const typeW = L.fonts.sansBold.widthOfTextAtSize("Document", 8) + 10;
  L.page.drawRectangle({
    x: MARGIN_L + 80 - typeW - 4, y: L.y - 13, width: typeW, height: 11,
    color: COLORS.white, borderColor: COLORS.gold, borderWidth: 0.7,
  });
  L.page.drawText("Document", {
    x: MARGIN_L + 80 - typeW + 1, y: L.y - 11, size: 8, font: L.fonts.sansBold, color: COLORS.gold,
  });
  // Title (bold serif)
  let dy = L.y - 12;
  for (const ln of titleLines) {
    L.page.drawText(ln, {
      x: detailX, y: dy, size: 10, font: L.fonts.serifBold, color: COLORS.text,
    });
    dy -= 13;
  }
  // Sub (italic dim)
  for (const ln of subLines) {
    L.page.drawText(ln, {
      x: detailX, y: dy, size: 9, font: L.fonts.serifItalic, color: COLORS.dim,
    });
    dy -= 11;
  }
  for (const ln of descLines) {
    L.page.drawText(ln, {
      x: detailX, y: dy, size: 9, font: L.fonts.serifItalic, color: COLORS.dim,
    });
    dy -= 11;
  }
  L.advance(rowH);
}

function drawEmployeeLevelDocs(L, input) {
  if (!input.employeeLevelDocs || input.employeeLevelDocs.length === 0) return;
  drawSectionTitle(L, "Employee-Level Documents");
  L.drawWrapped(
    "The following documents are on file for the subject employee but are not tagged to any specific ride.",
    { font: L.fonts.serif, size: 11, color: COLORS.text, lineHeight: 1.5 }
  );
  L.advance(8);
  for (const d of input.employeeLevelDocs) {
    drawDocRow(L, d);
  }
  L.advance(10);
}

function drawSignature(L, input) {
  // Ensure signature block stays on one page if possible
  L.reserve(180);
  drawSectionTitle(L, "Certification & Signature");
  L.drawWrapped(
    `I certify that the information presented in this record was produced from the operational training database of Las Vegas Mini Grand Prix on ${fmtDateFull(input.generatedDate)}, and accurately reflects the records on file as of that date.`,
    { font: L.fonts.serif, size: 11, color: COLORS.text, lineHeight: 1.55 }
  );
  L.advance(16);
  // Signature lines: prepared by / date / name / title
  const lineY1 = L.y - 30;
  const lineY2 = L.y - 70;
  const col1X = MARGIN_L;
  const col2X = MARGIN_L + USABLE_W * 0.55;
  const col1W = USABLE_W * 0.50;
  const col2W = USABLE_W * 0.40;
  // Row 1
  L.page.drawLine({
    start: { x: col1X, y: lineY1 }, end: { x: col1X + col1W, y: lineY1 },
    thickness: 0.5, color: COLORS.text,
  });
  L.page.drawText("PREPARED BY (SIGNATURE)", {
    x: col1X, y: lineY1 - 12, size: 7.5, font: L.fonts.sansBold, color: COLORS.dimmer,
  });
  L.page.drawLine({
    start: { x: col2X, y: lineY1 }, end: { x: col2X + col2W, y: lineY1 },
    thickness: 0.5, color: COLORS.text,
  });
  L.page.drawText("DATE", {
    x: col2X, y: lineY1 - 12, size: 7.5, font: L.fonts.sansBold, color: COLORS.dimmer,
  });
  // Row 2
  L.page.drawLine({
    start: { x: col1X, y: lineY2 }, end: { x: col1X + col1W, y: lineY2 },
    thickness: 0.5, color: COLORS.text,
  });
  L.page.drawText("NAME (PRINT)", {
    x: col1X, y: lineY2 - 12, size: 7.5, font: L.fonts.sansBold, color: COLORS.dimmer,
  });
  L.page.drawLine({
    start: { x: col2X, y: lineY2 }, end: { x: col2X + col2W, y: lineY2 },
    thickness: 0.5, color: COLORS.text,
  });
  L.page.drawText("TITLE / ROLE", {
    x: col2X, y: lineY2 - 12, size: 7.5, font: L.fonts.sansBold, color: COLORS.dimmer,
  });
  L.advance(110);
  // Gold separator
  L.reserve(10);
  L.page.drawLine({
    start: { x: MARGIN_L, y: L.y }, end: { x: PAGE_W - MARGIN_R, y: L.y },
    thickness: 1.5, color: COLORS.gold,
  });
  L.advance(20);
}

/* ---------- attachment handling ---------- */
async function appendAttachments(pdf, input, fonts, meta, onProgress) {
  const refs = input.referencedDocs || [];
  if (refs.length === 0) return;

  // Index page listing all attachments
  const indexPage = pdf.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN_T;
  // Running header
  indexPage.drawText("LAS VEGAS MINI GRAND PRIX", {
    x: MARGIN_L, y: PAGE_H - 32, size: 8, font: fonts.sansBold, color: COLORS.dimmer,
  });
  const rightLbl = "Compliance Training Record";
  const rwW = fonts.sans.widthOfTextAtSize(rightLbl, 8);
  indexPage.drawText(rightLbl, {
    x: PAGE_W - MARGIN_R - rwW, y: PAGE_H - 32, size: 8, font: fonts.sans, color: COLORS.dimmer,
  });
  indexPage.drawLine({
    start: { x: MARGIN_L, y: PAGE_H - 40 }, end: { x: PAGE_W - MARGIN_R, y: PAGE_H - 40 },
    thickness: 0.5, color: COLORS.rule,
  });
  // Title
  indexPage.drawText("Appendix: Supporting Documents", {
    x: MARGIN_L, y: y - 20, size: 16, font: fonts.serifBold, color: COLORS.text,
  });
  y -= 35;
  indexPage.drawLine({
    start: { x: MARGIN_L, y: y }, end: { x: PAGE_W - MARGIN_R, y: y },
    thickness: 0.5, color: COLORS.rule,
  });
  y -= 16;
  const introLines = wrapText(
    `The following ${refs.length} document${refs.length === 1 ? "" : "s"} ${refs.length === 1 ? "is" : "are"} included in this packet as supporting evidence. Each is preceded by a separator page identifying it.`,
    fonts.serif, 11, USABLE_W
  );
  for (const ln of introLines) {
    indexPage.drawText(ln, { x: MARGIN_L, y: y - 11, size: 11, font: fonts.serif, color: COLORS.text });
    y -= 15;
  }
  y -= 8;
  // List
  for (let i = 0; i < refs.length; i++) {
    const d = refs[i];
    if (y < MARGIN_B + 30) {
      // continue on next page (uncommon)
      break;
    }
    const num = String(i + 1).padStart(2, "0");
    indexPage.drawText(`${num}.`, {
      x: MARGIN_L, y: y - 11, size: 10, font: fonts.mono, color: COLORS.dimmer,
    });
    const titleText = `[${d.category}] ${d.filename}`;
    const titleLines = wrapText(titleText, fonts.serifBold, 10, USABLE_W - 30);
    let tY = y - 11;
    for (const ln of titleLines) {
      indexPage.drawText(ln, { x: MARGIN_L + 24, y: tY, size: 10, font: fonts.serifBold, color: COLORS.text });
      tY -= 13;
    }
    const meta2parts = [`Uploaded ${fmtDateShort(d.uploaded_at)}`];
    if (d.issued_at) meta2parts.push(`Issued ${fmtDateShort(d.issued_at)}`);
    if (d.expires_at) meta2parts.push(`Expires ${fmtDateShort(d.expires_at)}`);
    if (d.rides && d.rides.length > 0) meta2parts.push(`Tagged: ${d.rides.map((rk) => input.rideLabelMap[rk] || rk).join(", ")}`);
    const metaLines = wrapText(meta2parts.join(" · "), fonts.serifItalic, 9, USABLE_W - 30);
    for (const ln of metaLines) {
      indexPage.drawText(ln, { x: MARGIN_L + 24, y: tY, size: 9, font: fonts.serifItalic, color: COLORS.dim });
      tY -= 11;
    }
    y = tY - 8;
  }

  // Per-attachment: separator page + content
  for (let i = 0; i < refs.length; i++) {
    const d = refs[i];
    if (onProgress) onProgress(i, refs.length, d.filename);

    // Separator page
    const sep = pdf.addPage([PAGE_W, PAGE_H]);
    // Running header
    sep.drawText("LAS VEGAS MINI GRAND PRIX", {
      x: MARGIN_L, y: PAGE_H - 32, size: 8, font: fonts.sansBold, color: COLORS.dimmer,
    });
    sep.drawText(rightLbl, {
      x: PAGE_W - MARGIN_R - rwW, y: PAGE_H - 32, size: 8, font: fonts.sans, color: COLORS.dimmer,
    });
    sep.drawLine({
      start: { x: MARGIN_L, y: PAGE_H - 40 }, end: { x: PAGE_W - MARGIN_R, y: PAGE_H - 40 },
      thickness: 0.5, color: COLORS.rule,
    });
    // Center the attachment number + title
    const labelN = `ATTACHMENT ${String(i + 1).padStart(2, "0")} OF ${String(refs.length).padStart(2, "0")}`;
    const labelNW = fonts.sansBold.widthOfTextAtSize(labelN, 10);
    sep.drawText(labelN, {
      x: (PAGE_W - labelNW) / 2, y: PAGE_H / 2 + 60, size: 10, font: fonts.sansBold, color: COLORS.gold,
    });
    // Filename
    const titleLines = wrapText(d.filename, fonts.serifBold, 18, USABLE_W);
    let tY = PAGE_H / 2 + 30;
    for (const ln of titleLines) {
      const w = fonts.serifBold.widthOfTextAtSize(ln, 18);
      sep.drawText(ln, { x: (PAGE_W - w) / 2, y: tY, size: 18, font: fonts.serifBold, color: COLORS.text });
      tY -= 22;
    }
    // Category
    const catText = d.category;
    const catW = fonts.serif.widthOfTextAtSize(catText, 12);
    sep.drawText(catText, {
      x: (PAGE_W - catW) / 2, y: tY - 10, size: 12, font: fonts.serifItalic, color: COLORS.dim,
    });
    // Metadata table
    let mY = tY - 50;
    const metaRows = [
      ["Uploaded", fmtDateFull(d.uploaded_at)],
      d.issued_at ? ["Issued", fmtDateFull(d.issued_at)] : null,
      d.expires_at ? ["Expires", fmtDateFull(d.expires_at)] : null,
      (d.rides && d.rides.length > 0) ? ["Tagged rides", d.rides.map((rk) => input.rideLabelMap[rk] || rk).join(", ")] : null,
      d.description ? ["Description", d.description] : null,
    ].filter(Boolean);
    for (const [label, value] of metaRows) {
      sep.drawText(label.toUpperCase(), {
        x: PAGE_W / 2 - 130, y: mY, size: 8, font: fonts.sansBold, color: COLORS.dimmer,
      });
      const vLines = wrapText(value, fonts.serif, 11, 200);
      let vY = mY;
      for (const ln of vLines) {
        sep.drawText(ln, { x: PAGE_W / 2 + 10, y: vY, size: 11, font: fonts.serif, color: COLORS.text });
        vY -= 14;
      }
      mY = Math.min(mY - 18, vY - 4);
    }

    // Now embed actual content
    const blob = d.blob;
    if (!blob) {
      // Couldn't fetch
      sep.drawText("(File could not be retrieved for embedding)", {
        x: MARGIN_L, y: MARGIN_B + 40, size: 10, font: fonts.serifItalic, color: COLORS.red,
      });
      continue;
    }
    const mimeType = (blob.type || "").toLowerCase();
    const fnameLower = (d.filename || "").toLowerCase();
    try {
      if (mimeType === "application/pdf" || fnameLower.endsWith(".pdf")) {
        // Embed PDF pages
        const arrayBuf = await blob.arrayBuffer();
        const srcPdf = await PDFDocument.load(arrayBuf, { ignoreEncryption: true });
        const pageIndices = srcPdf.getPageIndices();
        const copied = await pdf.copyPages(srcPdf, pageIndices);
        for (const p of copied) {
          pdf.addPage(p);
        }
      } else if (mimeType.startsWith("image/") || /\.(png|jpg|jpeg|gif|webp)$/i.test(fnameLower)) {
        // Embed image
        const arrayBuf = await blob.arrayBuffer();
        let img;
        if (mimeType === "image/png" || fnameLower.endsWith(".png")) {
          img = await pdf.embedPng(arrayBuf);
        } else {
          // Try JPG; if it fails (e.g., GIF/WEBP), fall through to a note page
          try { img = await pdf.embedJpg(arrayBuf); }
          catch { img = null; }
        }
        if (img) {
          const imgPage = pdf.addPage([PAGE_W, PAGE_H]);
          // Fit image to page while preserving aspect ratio
          const availW = USABLE_W;
          const availH = PAGE_H - MARGIN_T - MARGIN_B - 20;
          const scale = Math.min(availW / img.width, availH / img.height, 1);
          const w = img.width * scale;
          const h = img.height * scale;
          imgPage.drawImage(img, {
            x: (PAGE_W - w) / 2, y: (PAGE_H - h) / 2, width: w, height: h,
          });
        } else {
          // Unembeddable image format — leave a note
          const note = pdf.addPage([PAGE_W, PAGE_H]);
          note.drawText("Image format not embeddable in PDF.", {
            x: MARGIN_L, y: PAGE_H / 2, size: 12, font: fonts.serifItalic, color: COLORS.dim,
          });
          note.drawText(`Original file: ${d.filename}`, {
            x: MARGIN_L, y: PAGE_H / 2 - 20, size: 10, font: fonts.serif, color: COLORS.dim,
          });
        }
      } else {
        // Non-PDF, non-image: just note
        const note = pdf.addPage([PAGE_W, PAGE_H]);
        note.drawText("This file type cannot be embedded in PDF format.", {
          x: MARGIN_L, y: PAGE_H / 2, size: 12, font: fonts.serifItalic, color: COLORS.dim,
        });
        note.drawText(`Original file: ${d.filename}`, {
          x: MARGIN_L, y: PAGE_H / 2 - 20, size: 10, font: fonts.serif, color: COLORS.dim,
        });
        note.drawText(`MIME type: ${mimeType || "unknown"}`, {
          x: MARGIN_L, y: PAGE_H / 2 - 36, size: 10, font: fonts.serif, color: COLORS.dim,
        });
        note.drawText("This file is included in the document bundle ZIP (if downloaded separately).", {
          x: MARGIN_L, y: PAGE_H / 2 - 56, size: 10, font: fonts.serif, color: COLORS.dim,
        });
      }
    } catch (e) {
      const note = pdf.addPage([PAGE_W, PAGE_H]);
      note.drawText("Error embedding this attachment.", {
        x: MARGIN_L, y: PAGE_H / 2, size: 12, font: fonts.serifItalic, color: COLORS.red,
      });
      note.drawText(`Original file: ${d.filename}`, {
        x: MARGIN_L, y: PAGE_H / 2 - 20, size: 10, font: fonts.serif, color: COLORS.dim,
      });
      note.drawText(`Error: ${e.message || String(e)}`, {
        x: MARGIN_L, y: PAGE_H / 2 - 36, size: 9, font: fonts.mono, color: COLORS.dim,
      });
    }
  }
}

/* ---------- footers (page numbering pass) ---------- */
async function applyFooters(pdf, meta) {
  const fontMono = await pdf.embedFont(StandardFonts.Courier);
  // wrap widthOfTextAtSize for this font too
  const origWidth = fontMono.widthOfTextAtSize.bind(fontMono);
  fontMono.widthOfTextAtSize = function (text, size) {
    return origWidth(safeText(text), size);
  };
  const pages = pdf.getPages();
  const total = pages.length;
  for (let i = 0; i < total; i++) {
    const p = pages[i];
    const { width: w, height: h } = p.getSize();
    p.drawLine({
      start: { x: MARGIN_L, y: MARGIN_B - 12 }, end: { x: w - MARGIN_R, y: MARGIN_B - 12 },
      thickness: 0.5, color: COLORS.rule,
    });
    const left = meta.reportId || "";
    p.drawText(left, { x: MARGIN_L, y: MARGIN_B - 24, size: 7.5, font: fontMono, color: COLORS.dimmer });
    const mid = `Page ${i + 1} of ${total}`;
    const midW = fontMono.widthOfTextAtSize(mid, 7.5);
    p.drawText(mid, { x: (w - midW) / 2, y: MARGIN_B - 24, size: 7.5, font: fontMono, color: COLORS.dimmer });
    const right = `Generated ${meta.generatedDate}`;
    const rW = fontMono.widthOfTextAtSize(right, 7.5);
    p.drawText(right, { x: w - MARGIN_R - rW, y: MARGIN_B - 24, size: 7.5, font: fontMono, color: COLORS.dimmer });
  }
}

/* ============================================================
   PUBLIC API
   ============================================================ */
export async function buildCompliancePdf(input, onProgress) {
  // input = {
  //   reportId, employee: {name, role, department},
  //   asOfDate, generatedDate, ridesCoveredText,
  //   incidentRef,
  //   rides: [{ ride: {key, label}, assertion: {level, basis, supportingEvents}, events: [{date, type, detail}], docs: [...] }],
  //   employeeLevelDocs: [...],
  //   referencedDocs: [{...doc, blob}],  // pre-fetched blobs
  //   rideLabelMap: { ride_key: label }
  // }
  const pdf = await PDFDocument.create();
  pdf.setTitle(safeText(`Compliance Training Record - ${input.employee.name}`));
  pdf.setAuthor("Las Vegas Mini Grand Prix");
  pdf.setSubject(safeText(`Point-in-time training record as of ${fmtDateFull(input.asOfDate)}`));
  pdf.setCreator("LVMGP Ride Operator Training System");
  pdf.setCreationDate(new Date());

  // Patch addPage so every page's drawText/drawRectangle/drawImage etc are safe.
  // We wrap drawText to sanitize string input automatically.
  const origAddPage = pdf.addPage.bind(pdf);
  pdf.addPage = function (...args) {
    const page = origAddPage(...args);
    const origDrawText = page.drawText.bind(page);
    page.drawText = function (text, opts) {
      return origDrawText(safeText(text), opts);
    };
    return page;
  };

  // Patch font.widthOfTextAtSize to sanitize, since the same encoding constraint applies
  async function safeFont(name) {
    const f = await pdf.embedFont(name);
    const origWidth = f.widthOfTextAtSize.bind(f);
    f.widthOfTextAtSize = function (text, size) {
      return origWidth(safeText(text), size);
    };
    return f;
  }

  const fonts = {
    serif: await safeFont(StandardFonts.TimesRoman),
    serifBold: await safeFont(StandardFonts.TimesRomanBold),
    serifItalic: await safeFont(StandardFonts.TimesRomanItalic),
    sans: await safeFont(StandardFonts.Helvetica),
    sansBold: await safeFont(StandardFonts.HelveticaBold),
    mono: await safeFont(StandardFonts.Courier),
  };

  const meta = {
    reportId: input.reportId,
    employeeName: input.employee.name,
    asOfDate: fmtDateFull(input.asOfDate),
    generatedDate: fmtDateFull(input.generatedDate),
  };

  const L = new Layout(pdf, fonts);
  L.setMeta(meta);

  // Body of dossier
  drawTitleBlock(L, input);
  drawAttestation(L, input);
  drawCertSummary(L, input);
  drawAuditTrail(L, input);
  drawEmployeeLevelDocs(L, input);
  drawSignature(L, input);

  // Attachments
  if (onProgress) onProgress(0, (input.referencedDocs || []).length, "Embedding attachments…");
  await appendAttachments(pdf, input, fonts, meta, onProgress);

  // Page numbers (final pass — need total page count)
  await applyFooters(pdf, meta);

  const bytes = await pdf.save();
  return new Blob([bytes], { type: "application/pdf" });
}
